import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CoopQuizSession } from '../hooks/useCoopQuiz';

interface CoopQuizGameProps {
  session: CoopQuizSession;
  myPlayerNumber: 1 | 2;
  onSubmitAnswer: (answer: 'mauricio' | 'novia') => void;
  onAdvanceQuestion: () => void;
  onUpdateScores: (player1Correct: boolean, player2Correct: boolean) => void;
  onEndSession: () => void;
  slot1Profile: any;
  slot2Profile: any;
}

export const CoopQuizGame: React.FC<CoopQuizGameProps> = ({
  session,
  myPlayerNumber,
  onSubmitAnswer,
  onAdvanceQuestion,
  onUpdateScores,
  onEndSession,
  slot1Profile,
  slot2Profile
}) => {
  const [showingResults, setShowingResults] = useState(false);

  const handleEndSession = () => {
    if (window.confirm('¿Seguro que quieres terminar esta sesión? El juego terminará para ambos jugadores.')) {
      onEndSession();
    }
  };

  const currentQ = session.currentQuestion;
  const track = session.questions[currentQ];
  const questionKey = `question${currentQ}`;
  const answers = session.answers[questionKey] || {};

  const myAnswer = myPlayerNumber === 1 ? answers.player1 : answers.player2;
  const otherAnswer = myPlayerNumber === 1 ? answers.player2 : answers.player1;
  const bothAnswered = answers.player1 !== undefined && answers.player2 !== undefined;

  // Handle answer submission
  const handleAnswer = (answer: 'mauricio' | 'novia') => {
    if (myAnswer !== undefined) return; // Already answered
    onSubmitAnswer(answer);
  };

  // When both players answer, show results and update scores
  useEffect(() => {
    if (bothAnswered && !showingResults) {
      setShowingResults(true);

      // Calculate if each player was correct
      const player1Correct = answers.player1 === track.owner;
      const player2Correct = answers.player2 === track.owner;

      // Update scores (only player 1 updates to avoid double updates)
      if (myPlayerNumber === 1) {
        onUpdateScores(player1Correct, player2Correct);
      }
    }
  }, [bothAnswered, showingResults, myPlayerNumber, track.owner, answers, onUpdateScores]);

  // Reset showing results when question changes
  useEffect(() => {
    setShowingResults(false);
  }, [currentQ]);

  const player1Correct = answers.player1 === track.owner;
  const player2Correct = answers.player2 === track.owner;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-400">
          Pregunta {currentQ + 1} de {session.questions.length}
        </div>
        <div className="flex gap-4 text-sm">
          <span className={`font-bold ${myPlayerNumber === 1 ? 'text-green-400' : 'text-pink-400'}`}>
            Tú: {myPlayerNumber === 1 ? session.scores.player1 : session.scores.player2}
          </span>
          <span className={`font-bold ${myPlayerNumber === 2 ? 'text-green-400' : 'text-pink-400'}`}>
            Otro: {myPlayerNumber === 1 ? session.scores.player2 : session.scores.player1}
          </span>
        </div>
      </div>

      {/* End Session Button - Always visible */}
      <div className="flex justify-center">
        <button
          onClick={handleEndSession}
          className="px-4 py-2 bg-red-500/20 border border-red-500 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm font-semibold"
        >
          ❌ Terminar Sesión
        </button>
      </div>

      {/* Question */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-purple-400 mb-6">
          ¿De quién es esta canción?
        </h3>

        <motion.div
          key={currentQ}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/5 p-6 rounded-lg mb-6"
        >
          <img
            src={track.albumImage || 'https://via.placeholder.com/200x200/1DB954/white?text=♪'}
            alt={track.name}
            className="w-32 h-32 mx-auto rounded-lg mb-4"
          />
          <h4 className="text-xl font-bold text-white mb-2">{track.name}</h4>
          <p className="text-gray-400">{track.artist}</p>
        </motion.div>

        {/* Answer Buttons or Results */}
        {!showingResults ? (
          <div>
            {myAnswer === undefined ? (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleAnswer('mauricio')}
                  className="p-4 bg-green-500/20 border border-green-500 rounded-lg hover:bg-green-500/30 transition-all"
                >
                  <div className="text-lg font-bold text-green-400">
                    {slot1Profile?.display_name || 'Jugador 1'}
                  </div>
                </button>
                <button
                  onClick={() => handleAnswer('novia')}
                  className="p-4 bg-pink-500/20 border border-pink-500 rounded-lg hover:bg-pink-500/30 transition-all"
                >
                  <div className="text-lg font-bold text-pink-400">
                    {slot2Profile?.display_name || 'Jugador 2'}
                  </div>
                </button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center p-6 bg-blue-500/20 rounded-lg border border-blue-500/50"
              >
                <div className="text-4xl mb-3">⏳</div>
                <p className="text-lg font-bold text-blue-400">
                  Esperando al otro jugador...
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Tu respuesta: <span className="font-bold">{myAnswer === 'mauricio' ? slot1Profile?.display_name : slot2Profile?.display_name}</span>
                </p>
              </motion.div>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            {/* Results for both players */}
            <div className="grid grid-cols-2 gap-4">
              {/* Player 1 Result */}
              <div className={`p-4 rounded-lg border-2 ${player1Correct ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                <div className="text-center">
                  <div className="text-4xl mb-2">{player1Correct ? '✅' : '❌'}</div>
                  <p className="font-bold text-green-400">{slot1Profile?.display_name || 'Jugador 1'}</p>
                  <p className="text-sm text-gray-400">
                    Eligió: {answers.player1 === 'mauricio' ? slot1Profile?.display_name : slot2Profile?.display_name}
                  </p>
                </div>
              </div>

              {/* Player 2 Result */}
              <div className={`p-4 rounded-lg border-2 ${player2Correct ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                <div className="text-center">
                  <div className="text-4xl mb-2">{player2Correct ? '✅' : '❌'}</div>
                  <p className="font-bold text-pink-400">{slot2Profile?.display_name || 'Jugador 2'}</p>
                  <p className="text-sm text-gray-400">
                    Eligió: {answers.player2 === 'mauricio' ? slot1Profile?.display_name : slot2Profile?.display_name}
                  </p>
                </div>
              </div>
            </div>

            {/* Correct Answer */}
            <div className="text-center p-4 bg-purple-500/20 rounded-lg">
              <p className="text-sm text-gray-400">La respuesta correcta es:</p>
              <p className="text-lg font-bold text-purple-400">{track.ownerName}</p>
            </div>

            {/* Next Question Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onAdvanceQuestion}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
            >
              Siguiente Pregunta →
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};
